import {
     BASE_URL,
    type SeoService, type PageOverrides,
    BUSINESS_ID_ABS, POSTER_URL,
    SECTION_TWO_IMG, SECTION_TWO_HORIZONTAL_IMG, albumsOrder,
    withBetNoLeadSpace,
    GALLERY_ID_ABS,
  } from './core';

  import {
    BUSINESS,HTML_LANG_FULL,SECTION_TWO_TITLE,SECTION_TWO_DESCRIPTION,SECTION_TWO_ADDITIONAL_TEXT,SECTION_TWO_IMG_ALT,SECTION_TWO_HORIZONTAL_IMG_ALT, SECTION_FOUR_CONTACT_BUTTONS,SECTION_FOUR_SERVICE_BUTTONS,SECTION_FOUR_EVENT_BUTTONS,
 } from '../i18n/locales/i18n.he';

  import { type SeoCity } from './geo';
import { galleryNodeId, normalizeGalleryBase } from './jsonld-utils';
import { durationToIso8601, galleryVideoSeoItems, toSeoUrl } from './gallery-video-seo';

const normJsonLdText = (value: unknown) =>
  String(value ?? '').replace(/\s+/g, ' ').trim();

const renderPersonalDjHeadline = (topic: string) => {
  const match = topic.match(/^(.+?)\s+(ב.+)$/);
  const service = normJsonLdText(match?.[1] ?? topic);
  const location = normJsonLdText(match?.[2] ?? '');
  const serviceWithArticle = service.replace(/^(דיג׳יי|די ג׳יי)\s+/, 'ה$1 ');

  return normJsonLdText(`למה לבחור בי להיות ${serviceWithArticle} שלכם${location ? ` ${location}` : ''}?`);
};

const renderSection2Headline = (heading: string, topic: string) => {
  const cleanHeading = normJsonLdText(heading);
  const cleanTopic = normJsonLdText(topic);

  if (!cleanHeading || !cleanTopic || cleanHeading.includes(cleanTopic)) {
    return cleanHeading;
  }

  if (cleanHeading === 'הסיפור שלי' && /דיג׳יי|די ג׳יי/.test(cleanTopic)) {
    return renderPersonalDjHeadline(cleanTopic);
  }

  return cleanHeading;
};


/** S1: Hero / הצגה – נשתמש ב-WebPageElement עם עוגן יציב */
export function buildSection1JsonLd(
    keyword: string,
    city: SeoCity,
    opts?: {
      section1?: PageOverrides['section1'];
      includeVideoRef?: boolean;    // true אם בגרף הראשי הוזרק VideoObject עם @id "#video"
      inLanguage?: string;          // ברירת מחדל: he-IL
      posterUrl?: string;           // ברירת מחדל: POSTER_URL
      videoIdAbs?: string;          // מזהה מוחלט לוידאו אם קיים
    }
  ) {
    const inLanguage = opts?.inLanguage ?? HTML_LANG_FULL ?? 'he-IL';
    const poster     = opts?.posterUrl ?? POSTER_URL;
  
    const keywordForHeadline = keyword.replace(/דיג׳יי/g, 'די ג׳יי');
    const rawHeadline = opts?.section1?.title ?? `${keywordForHeadline} ${withBetNoLeadSpace(city)}`;
    const headline = rawHeadline.replace(/דיג׳יי/g, 'די ג׳יי').replace(/\s{2,}/g, ' ').trim();
    const lines    = opts?.section1?.descriptionLines ?? [];
    const description =
      (Array.isArray(lines) && lines.length ? lines.join(' ') : undefined) ||
      `${keyword} ${withBetNoLeadSpace(city)} – מוזיקה מותאמת אישית ואנרגיה על הרחבה.`;
  
    const node: any = {
      '@type': 'WebPageElement',
      '@id': '#s1-hero',
      inLanguage,
      name: 'סקשן פתיח (Hero)',
      headline,
      description,
      image: poster,
    };
    if (opts?.includeVideoRef) {
      node.video = { '@id': opts.videoIdAbs ?? '#video' };
    }
    return node;
  }
  
  /** S2: מידע / למה אני – ממפים את כותרות ה-H3 לאייטמים */
  export function buildSection2JsonLd(overrides?: PageOverrides['section2'], topic = '') {
    const title          = overrides?.title ?? SECTION_TWO_TITLE;
    const headline       = renderSection2Headline(title, topic);
    const descPrimary    = overrides?.description ?? SECTION_TWO_DESCRIPTION;
    const descAdditional = overrides?.additionalText ?? SECTION_TWO_ADDITIONAL_TEXT;
  
    const portraitAlt    = normJsonLdText(overrides?.portraitAlt ?? SECTION_TWO_IMG_ALT);
    const horizontalAlt  = normJsonLdText(overrides?.horizontalAlt ?? SECTION_TWO_HORIZONTAL_IMG_ALT);
  
    const h3s         = overrides?.extraHeadings ?? [];
    const description = [descPrimary, descAdditional].filter(Boolean).join(' ');
  
    const headingsList =
      h3s.length
        ? {
            '@type': 'ItemList',
            '@id': '#s2-benefits',
            name: 'נקודות ערך מרכזיות',
            itemListElement: h3s.map((t, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: t,
            })),
          }
        : undefined;

        const associatedMedia = [
          SECTION_TWO_IMG
            ? { '@type': 'ImageObject', url: SECTION_TWO_IMG, caption: portraitAlt }
            : null,
          SECTION_TWO_HORIZONTAL_IMG
            ? { '@type': 'ImageObject', url: SECTION_TWO_HORIZONTAL_IMG, caption: horizontalAlt }
            : null,
        ].filter(Boolean);
  
        return {
          '@type': 'WebPageElement',
          '@id': '#s2-about',
          inLanguage: HTML_LANG_FULL ?? 'he-IL',
          name: 'סקשן אודות וערך מוסף',
          headline,
          description,
          about: { '@id': BUSINESS_ID_ABS },
          image: [SECTION_TWO_IMG, SECTION_TWO_HORIZONTAL_IMG].filter(Boolean),
          ...(associatedMedia.length ? { associatedMedia } : {}),
          ...(headingsList ? { hasPart: headingsList } : {}),
        };
  }
  
  /** S3: גלריה — CollectionPage עשיר עם ItemList של אלבומים ו-H3 אופציונלי */
/** S3: גלריה — רק עמוד אחד "בעלים" של ImageGallery; שאר העמודים מפנים אליו */
export function buildSection3JsonLd(
  overrides?: PageOverrides['section3'],
  opts?: { isGalleryOwner?: boolean; webpageId?: string }
) {
  const inLanguage = HTML_LANG_FULL ?? 'he-IL';

  const GALLERY_PAGE = normalizeGalleryBase(String(GALLERY_ID_ABS || ''));
  const GALLERY_NODE_ID = galleryNodeId(String(GALLERY_ID_ABS || '')); // ✅ SSOT
  
  const albumsList = {
    '@type': 'ItemList',
    '@id': `${GALLERY_NODE_ID}-albums`,
    name: 'אלבומי הגלריה',
    itemListElement: albumsOrder.map((name, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: { '@type': 'Thing', name }
    })),
  };

  const extraHeadings = (overrides?.extraHeadings ?? []).filter(Boolean);
  const headingsList = extraHeadings.length
    ? {
        '@type': 'ItemList',
        '@id': `${GALLERY_NODE_ID}-headings`,
        name: 'כותרות הגלריה',
        itemListElement: extraHeadings.map((t, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: normJsonLdText(t),
        })),
      }
    : undefined;

  const galleryVideos = galleryVideoSeoItems
    .filter((item) => item.id !== 'home-hero')
    .map((item) => ({
      '@type': 'VideoObject',
      '@id': `${GALLERY_NODE_ID}-video-${item.id}`,
      name: item.title,
      description: item.description,
      thumbnailUrl: [toSeoUrl(item.thumbnailUrl)],
      uploadDate: item.uploadDate,
      duration: durationToIso8601(item.durationSeconds),
      contentUrl: toSeoUrl(item.contentUrl),
      embedUrl: toSeoUrl(item.contentUrl),
      inLanguage,
      isFamilyFriendly: true,
      genre: item.albumName,
      potentialAction: [{ '@type': 'WatchAction', target: toSeoUrl(item.contentUrl) }],
    }));

  if (opts?.isGalleryOwner) {
    return {
      '@type': 'ImageGallery',
      '@id': GALLERY_NODE_ID,
      inLanguage,
      name: 'גלריית רגעים מהאירועים',
      url: GALLERY_PAGE,
      hasPart: headingsList ? [albumsList, headingsList, ...galleryVideos] : [albumsList, ...galleryVideos],
    };
  }

  return {
    '@type': 'WebPageElement',
    '@id': '#s3-gallery-ref',
    inLanguage,
    name: 'גלריית אירועים',
    isPartOf: { '@id': opts?.webpageId ?? '#webpage' },
    mentions: {
      '@type': 'ImageGallery',
      '@id': GALLERY_NODE_ID,
      url: GALLERY_PAGE,
    }
  };
}
  /** S4: יצירת קשר + קטגוריות + שירותים — LD עשיר וסמנטי */
  export function buildSection4JsonLdFull(section4: PageOverrides['section4'] | undefined) {
    const seo = section4?.seo;
    const navigateHref = section4?.navigateHref;
    const inLanguage = HTML_LANG_FULL ?? 'he-IL';
    const siteBase = String(BASE_URL).replace(/\/$/, '');
  
    const waText = (t: string) => encodeURIComponent(t.replace(/\r?\n/g, '\n'));
    const waHref = (base: string, text?: string) =>
      text ? `${base}${base.includes('?') ? '&' : '?'}text=${waText(text)}` : base;
  
    // ✅ חייב להיות לפני שימוש
    const whatsappHref = SECTION_FOUR_CONTACT_BUTTONS.whatsapp?.href;
  
    // ✅ חייב להיות לפני שימוש
    const buildWhatsAppAction = (label: string, prefill?: string) =>
      whatsappHref
        ? {
            '@type': 'CommunicateAction',
            target: waHref(whatsappHref, prefill ?? `שלום,\nמעוניין ב${label}.`),
            name: `פנייה לגבי ${label}`,
          }
        : undefined;
  
    const contactPoint: any = {
      '@type': 'ContactPoint',
      '@id': '#s4-contact',
      contactType: 'customer service',
      areaServed: 'Israel',
      availableLanguage: [inLanguage.split('-')[0]],
      ...(whatsappHref ? { potentialAction: buildWhatsAppAction('צ׳אט ב־WhatsApp') } : {}),
    };
  
    if (BUSINESS?.phone) contactPoint.telephone = BUSINESS.phone;
    if (BUSINESS?.email) contactPoint.email = BUSINESS.email;
    if (BASE_URL) contactPoint.url = siteBase;
  
    const sameAs: string[] = [];
    if (SECTION_FOUR_CONTACT_BUTTONS.instagram?.href) sameAs.push(SECTION_FOUR_CONTACT_BUTTONS.instagram.href);
    if (SECTION_FOUR_CONTACT_BUTTONS.facebook?.href) sameAs.push(SECTION_FOUR_CONTACT_BUTTONS.facebook.href);
    if (sameAs.length) contactPoint.sameAs = sameAs;
  
    const contactButtons = Object.entries(SECTION_FOUR_CONTACT_BUTTONS)
    .filter(([, cfg]) => !!cfg?.href && !!cfg?.label) as Array<
      [string, { label: string; href: string; prefill?: string }]
    >;
  
    const contactList = {
      '@type': 'ItemList',
      '@id': '#s4-contact-links',
      name: 'דרכי יצירת קשר',
      itemListElement: contactButtons.map(([key, cfg], i) => {
        const href =
          key === 'whatsapp'
            ? waHref(cfg.href, cfg.prefill)
            : key === 'navigate' && navigateHref
              ? navigateHref
              : cfg.href;
    
        return {
          '@type': 'ListItem',
          position: i + 1,
          name: cfg.label,
          item: {
            '@type': 'WebPage',
            name: cfg.label,
            url: href,
          },
        };
      }),
    };
  
    const eventsItems = (SECTION_FOUR_EVENT_BUTTONS ?? []).map((btn, i) => {
      const action = buildWhatsAppAction(btn.label, btn.prefill);
    
      return {
        '@type': 'ListItem',
        position: i + 1,
        name: btn.label,
        ...(action ? { potentialAction: action } : {}),
        ...(seo?.events?.[btn.label as keyof NonNullable<typeof seo>['events']]
          ? { description: seo.events![btn.label as keyof NonNullable<typeof seo>['events']] }
          : {}),
      };
    });
    
    const eventsList = {
      '@type': 'ItemList',
      '@id': '#s4-events',
      name: 'קטגוריות אירועים',
      itemListElement: eventsItems,
    };

    const servicesItems = (SECTION_FOUR_SERVICE_BUTTONS ?? []).map((btn, i) => {
      const action = buildWhatsAppAction(btn.label, btn.prefill);
  
      return {
        '@type': 'ListItem',
        position: i + 1,
        name: btn.label,
        ...(action ? { potentialAction: action } : {}),
        ...(seo?.services?.[btn.label as keyof NonNullable<typeof seo>['services']]
          ? { description: seo!.services![btn.label as keyof NonNullable<typeof seo>['services']] }
          : {}),
      };
    });
  
    const servicesList = {
      '@type': 'ItemList',
      '@id': '#s4-services',
      name: 'שירותים משלימים',
      itemListElement: servicesItems,
    };
  
    const introNodes: any[] = [];
    if (seo?.intros?.events) {
      introNodes.push({
        '@type': 'WebPageElement',
        '@id': '#s4-events-intro',
        inLanguage,
        name: 'Intro — אירועים',
        description: seo.intros.events,
      });
    }
    if (seo?.intros?.services) {
      introNodes.push({
        '@type': 'WebPageElement',
        '@id': '#s4-services-intro',
        inLanguage,
        name: 'Intro — שירותים',
        description: seo.intros.services,
      });
    }
  
    return [contactPoint, contactList, eventsList, servicesList, ...introNodes];
  }
  /** S5: תוכן ארוך (H3), FAQ, ותמונה — כולם תחת WebPageElement יחיד */
  export function buildSection5JsonLd(overrides?: PageOverrides['section5']) {
    const inLanguage = HTML_LANG_FULL ?? 'he-IL';
  
    const h3   = overrides?.h3Headings ?? [];
    const faqsRaw = overrides?.faqs ?? [];
    const img  = overrides?.extraImage;
  
    const faqs = (Array.isArray(faqsRaw) ? faqsRaw : [])
      .map((x) => ({
        q: typeof (x as any)?.q === 'string' ? (x as any).q.trim() : '',
        a: typeof (x as any)?.a === 'string' ? (x as any).a.trim() : '',
      }))
      .filter((x) => x.q.length >= 3 && x.a.length >= 3);
  
    const headingsList =
      h3.length
        ? {
            '@type': 'ItemList',
            '@id': '#s5-headings',
            name: 'נושאי התוכן המרכזיים',
            itemListElement: h3.map((t, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: t,
            })),
          }
        : undefined;
  
    const faqNode =
      faqs.length
        ? {
            '@type': 'FAQPage',
            '@id': '#faq',
            inLanguage,
            mainEntity: faqs.map(({ q, a }) => ({
              '@type': 'Question',
              name: normJsonLdText(q),
              acceptedAnswer: {
                '@type': 'Answer',
                text: normJsonLdText(a),
              },
            })),
          }
        : undefined;
  
    const imageNode =
      img?.src
        ? {
            '@type': 'ImageObject',
            '@id': '#s5-image',
            url: img.src,
            contentUrl: img.src,
            caption: normJsonLdText(img.alt || 'רגעים מהרחבה'),
            inLanguage,
          }
        : undefined;
  
    if (!headingsList && !faqNode && !imageNode) return null;
  
    return {
      '@type': 'WebPageElement',
      '@id': '#s5-content',
      inLanguage,
      name: normJsonLdText((overrides?.title || 'מידע נוסף ושאלות נפוצות').replace(/\s*\n+\s*/g, ' – ')),
      ...(typeof overrides?.subtitle === 'string' ? { description: normJsonLdText(overrides.subtitle) } : {}),
      hasPart: [headingsList, faqNode, imageNode].filter(Boolean),
    };
  }
  
  /**
   * S6: JSON-LD לסקשן הביקורות.
   * Google לא מציג Review/AggregateRating rich results לביקורות self-serving
   * על LocalBusiness, לכן אנחנו מסמנים את הסקשן בלבד ולא את הדירוגים עצמם.
   */
// src/constants/JsonLd.ts (או איפה שנמצא buildSection6JsonLd)

export function buildSection6JsonLd(
  overrides?: PageOverrides['section6'],
  opts?: {
    inLanguage?: string;
    service?: SeoService;
    city?: SeoCity;
    itemReviewedIdAbs?: string;
    itemReviewedName?: string;
    itemReviewedType?: 'Service' | 'LocalBusiness' | 'Organization' | 'Product';
    webpageIdAbs?: string;
    pageUrlAbs?: string; // ✅ canonical absolute with trailing slash recommended
  }
) {
  const lang = opts?.inLanguage ?? HTML_LANG_FULL ?? 'he-IL';

  const reviews = (overrides?.reviews ?? []) as Array<{
    rating: number;
    author?: string;
    text?: string;
    dateISO?: string;
    source?: 'google' | string;
    profileUrl?: string;
  }>;

  if (!Array.isArray(reviews) || reviews.length === 0) return null;

  // ✅ base for absolute ids (merge-safe BEFORE absolutizeGraphIds)
  const rawPageBase = String(opts?.pageUrlAbs || '').trim().split('#')[0];
  const pageBase = rawPageBase
    ? (rawPageBase.endsWith('/') ? rawPageBase : `${rawPageBase}/`)
    : '';
  const idAbs = (hash: string) => (pageBase ? `${pageBase}${hash}` : hash);

  // -------- itemReviewed --------
  const itemReviewedId = opts?.itemReviewedIdAbs || idAbs('#s6-itemReviewed');
  const itemReviewedType = opts?.itemReviewedType || 'LocalBusiness';

  const itemReviewedName =
    opts?.itemReviewedName ||
    (opts?.service && opts?.city
      ? `${opts.service} ${opts.city}`
      : overrides?.title || 'שירות/עסק');

  const siteUrlAbs = String(BASE_URL || '').replace(/\/$/, '');

  // ✅ If itemReviewedIdAbs passed (e.g. #business already exists in main graph) -> don't create another LocalBusiness node
  const shouldCreateItemReviewedNode = !opts?.itemReviewedIdAbs;

  const itemReviewedNode = shouldCreateItemReviewedNode
    ? {
        '@type': itemReviewedType,
        '@id': itemReviewedId,
        name: itemReviewedName,
        ...(siteUrlAbs ? { url: siteUrlAbs } : {}),
        ...(BUSINESS?.phone ? { telephone: BUSINESS.phone } : {}),
      }
    : undefined;

  // -------- Section container --------
  const webpageId = opts?.webpageIdAbs || idAbs('#webpage');

  const sectionNode = {
    '@type': 'WebPageElement',
    '@id': idAbs('#s6-reviews'),
    inLanguage: lang,
    name: overrides?.title || 'סקשן ביקורות',
    ...(overrides?.subtitle ? { description: overrides.subtitle } : {}),
    isPartOf: { '@id': webpageId },
    about: { '@id': itemReviewedId },
  };

  return [
    sectionNode,
    ...(itemReviewedNode ? [itemReviewedNode] : []),
  ];
}

export function buildSection7JsonLd(
  overrides?: PageOverrides['section7'],
  opts?: { webpageId?: string; pageUrlAbs?: string; inLanguage?: string }
) {
  const title = String(overrides?.title ?? '').trim();
  const subtitle = String(overrides?.subtitle ?? '').trim();
  const items = (overrides?.items ?? [])
    .filter((item) => item?.href && item?.title)
    .slice(0, 16);

  if (!title && !subtitle && !items.length) return null;

  const pageBase = String(opts?.pageUrlAbs ?? '').trim().replace(/\/?$/, '/');
  const absUrl = (href: string) => {
    const raw = String(href ?? '').trim();
    if (!raw) return raw;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (!pageBase) return raw;
    return `${BASE_URL.replace(/\/$/, '')}${raw.startsWith('/') ? raw : `/${raw}`}`;
  };

  return {
    '@type': 'WebPageElement',
    '@id': pageBase ? `${pageBase}#planning-guides` : '#planning-guides',
    inLanguage: opts?.inLanguage ?? HTML_LANG_FULL ?? 'he-IL',
    name: title || 'מדריכי תכנון',
    ...(subtitle ? { description: subtitle } : {}),
    ...(opts?.webpageId ? { isPartOf: { '@id': opts.webpageId } } : {}),
    ...(items.length
      ? {
          hasPart: {
            '@type': 'ItemList',
            '@id': pageBase ? `${pageBase}#planning-guide-links` : '#planning-guide-links',
            name: title || 'מדריכי תכנון',
            itemListElement: items.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.title,
              url: absUrl(item.href),
              ...(
                item.description || item.seoSummary || item.seoBullets?.length
                  ? {
                      description: [
                        item.description,
                        item.seoSummary,
                        ...(item.seoBullets ?? []),
                      ]
                        .filter(Boolean)
                        .join(' '),
                    }
                  : {}
              ),
            })),
          },
        }
      : {}),
  };
}
  
  /** אגרגטור: בונה JSON-LD לכל הסקשנים לפי הסדר (1→6) */
  export function buildAllSectionsJsonLd(
    service: SeoService,
    keyword: string,
    city: SeoCity,
    overrides: PageOverrides,
    opts?: {
      includeVideoRef?: boolean;
      inLanguage?: string;
      posterUrl?: string;
      videoIdAbs?: string;
      itemReviewedIdAbs?: string;
      pageUrlAbs?: string; 
      isGalleryOwner?: boolean; // ✅ חדש
    }
  ) {
 
    const s1 = buildSection1JsonLd(keyword, city, {
      section1: overrides.section1,
      includeVideoRef: opts?.includeVideoRef,
      inLanguage: opts?.inLanguage ?? HTML_LANG_FULL ?? 'he-IL',
      posterUrl: opts?.posterUrl ?? POSTER_URL,
      videoIdAbs: opts?.videoIdAbs,
    });
  
    const s2      = buildSection2JsonLd(
      overrides.section2,
      `${keyword} ${withBetNoLeadSpace(city)}`
    );
    const rawPageUrlAbs = String(opts?.pageUrlAbs ?? '').trim().split('#')[0];
    const pageUrlAbs = rawPageUrlAbs
      ? (rawPageUrlAbs.endsWith('/') ? rawPageUrlAbs : `${rawPageUrlAbs}/`)
      : '';
    const webpageId = pageUrlAbs ? `${pageUrlAbs}#webpage` : '#webpage';
    
    const s3 = buildSection3JsonLd(overrides.section3, {
      isGalleryOwner: !!opts?.isGalleryOwner,
      webpageId,
    });
    const s4Nodes = buildSection4JsonLdFull(overrides.section4) ?? [];
    const s5      = buildSection5JsonLd(overrides.section5);
  
    // מזהה מוחלט לישות העסק הראשית (#business) כ-itemReviewed

    const businessIdAbs = (opts?.itemReviewedIdAbs ?? BUSINESS_ID_ABS)!;

    const s6Nodes =
      buildSection6JsonLd(overrides.section6, {
        webpageIdAbs: webpageId,
        pageUrlAbs: opts?.pageUrlAbs, // ✅ חדש
        itemReviewedIdAbs: businessIdAbs,
        inLanguage: opts?.inLanguage ?? HTML_LANG_FULL ?? 'he-IL',
        service,
        city,
        itemReviewedType: 'LocalBusiness',
      }) ?? [];
    const s7 = buildSection7JsonLd(overrides.section7, {
      webpageId,
      pageUrlAbs: opts?.pageUrlAbs,
      inLanguage: opts?.inLanguage ?? HTML_LANG_FULL ?? 'he-IL',
    });
  
    return [s1, s2, s3, ...s4Nodes, ...s6Nodes, s7, s5].filter(Boolean) as object[];
  }
